/**
 * Tailwind Lit Web Component
 *
 * This component demonstrates how to use Tailwind CSS with Lit components.
 * It uses shadow DOM disabled mode to access global Tailwind styles.
 *
 * Key points:
 * 1. Set shadowRoot: false to disable Shadow DOM
 * 2. Use Tailwind classes directly in the template (no prefix in v4)
 *
 * Usage:
 * - <tailwind-card card-title="Hello" variant="info"></tailwind-card>
 */
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type TailwindCardVariant = 'default' | 'info' | 'success' | 'warning' | 'danger';

@customElement('tailwind-card')
export class TailwindCard extends LitElement {
  // Disable Shadow DOM to access global Tailwind styles
  override createRenderRoot() {
    return this;
  }

  @property({ type: String, attribute: 'card-title' })
  cardTitle = 'Tailwind Card';

  @property({ type: String })
  variant: TailwindCardVariant = 'default';

  @property({ type: Boolean, attribute: 'show-badge' })
  showBadge = true;

  private getVariantClasses(): string {
    const variants: Record<TailwindCardVariant, string> = {
      default: 'bg-white border-dashboard-gray-tertiary',
      info: 'bg-blue-50 border-app-blue',
      success: 'bg-green-50 border-success',
      warning: 'bg-amber-50 border-amber-500',
      danger: 'bg-red-50 border-danger',
    };
    return variants[this.variant];
  }

  private getIcon(): string {
    const icons: Record<TailwindCardVariant, string> = {
      default: '📦',
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      danger: '❌',
    };
    return icons[this.variant];
  }

  override render() {
    return html`
      <div class="block font-sans">
        <div class="rounded-lg p-4 my-2 shadow border transition-shadow hover:shadow-md ${this.getVariantClasses()}">
          <div class="flex items-center mb-3">
            <span class="w-6 h-6 mr-2 flex items-center justify-center">
              ${this.getIcon()}
            </span>
            <h4 class="text-base font-semibold m-0">${this.cardTitle}</h4>
            ${this.showBadge
              ? html`<span class="ml-auto inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-black/10">
                  Tailwind + Lit
                </span>`
              : ''}
          </div>
          <div class="text-sm leading-relaxed text-gray-700">
            <slot>Default content - pass content as children</slot>
          </div>
          <div class="mt-3 pt-3 border-t border-black/10 text-xs text-tertiary">
            Rendered by Lit with Tailwind CSS
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tailwind-card': TailwindCard;
  }
}

